"use client";
import clsx from "clsx";
import { type HTMLInputTypeAttribute, type InputHTMLAttributes, useState } from "react";
import { type Control, type FieldPath, type FieldValues, useController } from "react-hook-form";
import { Eye } from "../icons/Eye";
import { EyeSlash } from "../icons/EyeSlash";

type Props<T extends FieldValues> = {
  id: string;
  label: string;
  pattern?: string;
  name: FieldPath<T>;
  control: Control<T>;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  autoComplete?: string | undefined;
  type?: HTMLInputTypeAttribute;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: string;
  encryptedValue?: boolean;
  variant?: "primary" | "secondary";
  picto?: React.ReactNode;
  pictoDate?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;
export const Input = <T extends FieldValues>({
  id,
  label,
  control,
  placeholder,
  className,
  inputClassName,
  name,
  pattern,
  autoComplete,
  defaultValue,
  encryptedValue,
  type,
  disabled,
  readOnly,
  variant = "primary",
  picto,
  pictoDate = true,
  ...props
}: Props<T>) => {
  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({ name, control });
  const [currentType, setCurrentType] = useState<HTMLInputTypeAttribute>(type || "text");

  const handleSwitch = () => {
    if (currentType === "password") setCurrentType("text");
    if (currentType === "text") setCurrentType("password");
  };

  const encryptValue = (value?: string | null) => {
    if (!value) return "";

    const hiddenLength = value.length > 5 ? value.length - 3 : value.length >= 3 ? 2 : 3;
    const regexPattern = `^(\\w{${hiddenLength}})(\\w{${value.length - hiddenLength}})$`;
    const myRegex = new RegExp(regexPattern, "i");
    const result = value.replace(myRegex, `${Array.from({ length: hiddenLength }, () => "*").join("")}$2`);

    return result;
  };

  return (
    <div className={className}>
      {!!label && (
        <label
          htmlFor={id}
          className={clsx("text-xs font-normal font-sans", {
            "text-[#C92A2A]": error,
            "text-[#22201F]": !error,
          })}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          {...props}
          defaultValue={defaultValue}
          id={id}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          ref={ref}
          pattern={pattern}
          type={currentType}
          readOnly={readOnly}
          className={clsx(
            "rounded-lg border autofill:shadow-bg-white py-3 px-4 w-full text-base font-normal placeholder:text-tx-2 placeholder:font-normal placeholder:text-base",
            inputClassName,
            {
              "text-no border-no": error,
              "text-tx-1 border-bg-2": !error,
              "pr-12": type === "password",
              "bg-bg-1 ": variant === "secondary" && !disabled,
              "bg-white": variant === "primary" && !disabled,
              "bg-bg-1": disabled,
            },
          )}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        {type === "password" && (
          <div
            className="w-10 h-10 select-none absolute top-[5px] right-[5px] grid place-items-center"
            onClick={handleSwitch}
          >
            {currentType === "password" ? (
              <Eye className="w-6 h-6 text-primary" />
            ) : (
              <EyeSlash className="w-6 h-6 text-primary" />
            )}
          </div>
        )}
        {picto}
        {type === "date" && pictoDate && (
          <div
            className="w-10 h-10 select-none absolute top-1/2 -translate-y-1/2 right-[4px] grid place-items-center"
            onClick={handleSwitch}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="text-tx-2"
            >
              <path
                d="M12.6667 2.6665H3.33333C2.59695 2.6665 2 3.26346 2 3.99984V13.3332C2 14.0696 2.59695 14.6665 3.33333 14.6665H12.6667C13.403 14.6665 14 14.0696 14 13.3332V3.99984C14 3.26346 13.403 2.6665 12.6667 2.6665Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.6667 1.3335V4.00016"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.33325 1.3335V4.00016"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 6.6665H14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
      {error?.message && <p className="text-[#C92A2A] text-xs leading-5 mt-1">{error.message}</p>}
    </div>
  );
};
